import React, { useState } from 'react';
import { Button, Input, InputNumber } from 'antd';
import { CloseOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { ProductItemsProp } from '@app/interfaces/order';
import { formatMoney } from '@app/utils/utils';
import NumberInput from '../../common/NumberInput';
import { MdOutlineImage } from 'react-icons/md';

interface ProductTableRowProps {
  item: ProductItemsProp;
  index: number;
  onUpdateField: (id: string, field: string, value: string | number) => void;
  onDelete: (id: string) => void;
}

const ProductTableRow: React.FC<ProductTableRowProps> = ({ item, index, onUpdateField, onDelete }) => {
  const [showNote, setShowNote] = useState(false);
  const [editingQty, setEditingQty] = useState(false);

  const handleQtyInputChange = (val: number | null) => {
    const next = Math.max(1, Number(val) || 1);
    onUpdateField(item.id, 'qty', next);
  };

  const handleQtyBlur = () => setEditingQty(false);
  const handleQtyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') setEditingQty(false);
  };

  const handleDecrement = () => {
    const next = Math.max(1, item.qty - 1);
    onUpdateField(item.id, 'qty', next);
  };

  const handleIncrement = () => {
    onUpdateField(item.id, 'qty', item.qty + 1);
  };

  return (
    <div className="tw-border-0 tw-border-b tw-border-solid tw-border-[#f5f5f5] last:tw-border-b-0">
      <div className="tw-grid tw-grid-cols-[40px_1fr_90px_130px_90px_28px] tw-items-center tw-gap-2 tw-py-2.5">
        {/* Image */}
        <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-bg-[#f5f5f5] tw-text-[18px]">
          {item.image_url ? (
            <img
              src={(item as any).image_url}
              alt={item.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <MdOutlineImage style={{ fontSize: '18px', opacity: 0.35 }} />
          )}
        </div>

        {/* Name + SKU */}
        <div className="tw-min-w-0">
          <div className="tw-truncate tw-text-[13px] tw-font-semibold tw-leading-[1.3] tw-text-[#262626]" title={item.name}>
            {item.name}
          </div>
          <div className="tw-mt-px tw-text-[11px] tw-text-[#8c8c8c]">{item.product_code}</div>
        </div>

        {/* Qty stepper */}
        <div className="tw-flex tw-items-center tw-justify-center tw-gap-0.5">
          <Button
            type="text"
            size="small"
            icon={<MinusOutlined />}
            onClick={handleDecrement}
            style={{ padding: '0 4px', minWidth: 24 }}
          />
          {editingQty ? (
            <InputNumber
              autoFocus
              min={1}
              value={item.qty}
              onChange={handleQtyInputChange}
              onBlur={handleQtyBlur}
              onKeyDown={handleQtyKeyDown}
              controls={false}
              style={{ width: 52, textAlign: 'center', fontWeight: 600, fontSize: '13px' }}
              size="small"
            />
          ) : (
            <span
              title="Click để nhập số lượng"
              onClick={() => setEditingQty(true)}
              style={{ minWidth: 28, textAlign: 'center', fontWeight: 600, fontSize: '13px', cursor: 'text', padding: '0 4px' }}
            >
              {item.qty}
            </span>
          )}
          <Button
            type="text"
            size="small"
            icon={<PlusOutlined />}
            onClick={handleIncrement}
            style={{ padding: '0 4px', minWidth: 24 }}
          />
        </div>

        {/* Unit price */}
        <div>
          <NumberInput
            value={item.price}
            onChange={(val) => onUpdateField(item.id, 'price', val)}
            status={Number(item.price) === 0 ? 'error' : undefined}
          />
        </div>

        {/* Line total */}
        <div className="tw-whitespace-nowrap tw-text-right tw-text-[13px] tw-font-semibold tw-text-[#262626]">{formatMoney(item.amount)}</div>

        {/* Delete */}
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={() => onDelete(item.id)}
          className="tw-flex tw-items-center tw-justify-center"
        />
      </div>

      {/* Note toggle */}
      <div className="tw-col-span-full tw-pb-0.5 tw-pl-12">
        <Button
          type="link"
          size="small"
          onClick={() => setShowNote((s) => !s)}
          style={{ padding: 0, fontSize: '12px' }}
        >
          {showNote ? 'Ẩn ghi chú' : 'Thêm ghi chú'}
        </Button>
      </div>

      {showNote && (
        <div className="tw-col-span-full tw-pb-1.5 tw-pl-12 tw-pt-1">
          <Input.TextArea
            value={item.remark}
            placeholder="Ghi chú cho sản phẩm này..."
            rows={2}
            onChange={(e) => onUpdateField(item.id, 'remark', e.target.value)}
          />
        </div>
      )}
    </div>
  );
};

export default ProductTableRow;
